import type React from 'react'
import { useState, useMemo, useRef, useEffect, type MutableRefObject } from 'react'
import { Card, Popover } from 'antd'
import { Horizontal, Vertical, Texto, GraviGrid, GraviButton } from '@gravitate-js/excalibrr'
import { PlusOutlined, SettingOutlined } from '@ant-design/icons'
import { type SupplierConfig, useSupplierReconciliation } from '@api/useSupplierReconciliation'
import type { ColDef, GridApi } from 'ag-grid-community'
import { useMasterMetadata } from '@api/useMetadata/useMasterMetadata'
import type { SupplierConfigMetadataResponse } from '@api/useMetadata/types'
import { gridViewManager } from '@/utils/grid'
import { useUserSettings } from '@/hooks/settings'

import { AddExternalValuePopover } from '../shared/AddExternalValuePopover'
import UserSelectEditor from './editors/UserSelectEditor'
import { GlobalConfigDrawer } from './components/GlobalConfigDrawer'
import { SupplierConfigPanel } from './components/SupplierConfigPanel'
import { useManageSupplierConfigs } from './hooks/useManageSupplierConfigs'

import { getSupplierConfigColumns } from './columnDefs'

const STORAGE_KEY = 'supplier-configs-grid'

export function SupplierConfigTab() {
  const { updateSetting } = useUserSettings()
  const gridRef = useRef<GridApi>(null) as MutableRefObject<GridApi>
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierConfig | undefined>()
  const [globalConfigVisible, setGlobalConfigVisible] = useState(false)

  // Get supplier and global configuration data
  const { useConfigOverview } = useSupplierReconciliation()
  const { data, isLoading } = useConfigOverview()

  // Get metadata options
  const { data: metadata, isLoading: isMetadataLoading } =
    useMasterMetadata<SupplierConfigMetadataResponse>('supplier_config')

  const { toggleSupplierHold, handleMapSupplier, updateEP, removeMapping } = useManageSupplierConfigs(metadata)

  // Column definitions
  const columnDefs = useMemo(() => {
    return getSupplierConfigColumns()
  }, [])

  // Process data similar to OrdersGrid pattern - ensures stable data before rendering
  const processedData = useMemo(() => {
    if (!data?.rows) return []
    // Return processed data (currently no processing needed, but maintains pattern)
    return data.rows
  }, [data?.rows])

  // Update grid context and refresh cells when metadata loads
  useEffect(() => {
    if (gridRef.current && metadata?.users) {
      // Force refresh the assignee_username column when users are loaded
      // This will cause the cells to re-render with the updated context
      gridRef.current.refreshCells({
        columns: ['assignee_username'],
        force: true,
      })
    }
  }, [metadata?.users])

  return (
    <Horizontal style={{ height: '100%', flex: 1 }}>
      <Vertical flex={3} style={{ height: '100%' }}>
        {/* Alert is now displayed in the tab bar extra content */}

        <GraviGrid
          rowData={processedData}
          externalRef={gridRef}
          loading={!data?.rows || isMetadataLoading}
          updateEP={updateEP}
          onSelectionChanged={(event) => {
            const selectedRows = event.api.getSelectedRows()
            if (selectedRows.length > 0) {
              setSelectedSupplier(selectedRows[0])
            }
          }}
          onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
          {...gridViewManager(STORAGE_KEY, updateSetting)}
          agPropOverrides={{
            rowGroupPanelShow: 'never',
            columnDefs: columnDefs,
            getRowId: (params) => params.data?.supplier_id, // Use supplier_id instead of id
            rowSelection: 'single',
            components: {
              userSelectEditor: UserSelectEditor,
            },
            // Include user metadata and handlers in context
            context: {
              onRemoveMapping: removeMapping,
              onToggleHold: toggleSupplierHold,
              users: metadata?.users || [],
              metadata: metadata,
            },
          }}
          storageKey={STORAGE_KEY}
          controlBarProps={{
            title: 'Supplier Configs',
            actionButtons: (
              <ActionButtons
                handleMapSupplier={handleMapSupplier}
                setGlobalConfigVisible={setGlobalConfigVisible}
                counterpartyOptions={data?.rows || []}
              />
            ),
            rowCount: processedData?.length || 0,
          }}
        />
      </Vertical>

      {/* Right column - Config Forms */}
      <Vertical style={{ minWidth: 500, height: '100%' }} flex={2} bg='default'>
        {selectedSupplier ? (
          <SupplierConfigPanel selectedSupplier={selectedSupplier} defaultConfig={data?.default_config} />
        ) : (
          <Vertical gap={16} alignItems='center' justifyContent='center' style={{ height: '100%' }} bg='default'>
            <Texto category='h4'>No Supplier Selected</Texto>
            <Texto appearance='hint'>Select a supplier from the grid to view and manage their configuration</Texto>
          </Vertical>
        )}
      </Vertical>

      {/* Global Config Drawer */}
      <GlobalConfigDrawer
        visible={globalConfigVisible}
        onClose={() => setGlobalConfigVisible(false)}
        globalConfig={data?.default_config}
      />
    </Horizontal>
  )
}

const ActionButtons = ({
  handleMapSupplier,
  setGlobalConfigVisible,
  counterpartyOptions,
}: {
  handleMapSupplier: (values: {
    source_name: string
    source_id?: string
    counterparty_id?: string
  }) => Promise<void>
  setGlobalConfigVisible: (visible: boolean) => void
  counterpartyOptions: SupplierConfig[]
}) => {
  const [isOpen, setIsOpen] = useState(false)
  async function handleSave(values: {
    source_name: string
    source_id?: string
    counterparty_id?: string
  }) {
    await handleMapSupplier(values)
    setIsOpen(false)
  }
  return (
    <Horizontal gap={8}>
      <GraviButton
        icon={<SettingOutlined />}
        buttonText='Edit Global Config'
        theme2
        appearance='outlined'
        onClick={() => setGlobalConfigVisible(true)}
      />
      <Popover
        trigger='click'
        placement='bottomRight'
        open={isOpen}
        onOpenChange={setIsOpen}
        content={
          <AddExternalValuePopover type='supplier' onSubmit={handleSave} counterpartyOptions={counterpartyOptions} />
        }
      >
        <GraviButton icon={<PlusOutlined />} buttonText='Add Mapping' theme2 />
      </Popover>
    </Horizontal>
  )
}
